import { ref } from 'vue';

const POSTER_SEEK_TIME_SECONDS = 0.1;

/**
 * Generates a poster (thumbnail) image for a video attachment client-side,
 * by loading the video off-screen, seeking a fraction of a second in (so
 * the first frame isn't just black on some encodes), and capturing that
 * frame to a canvas.
 *
 * The backend does not generate video thumbnails (only image attachments
 * get a `thumb_url` - see `Attachment#file_metadata`), so this fills that
 * gap without needing a server-side change.
 *
 * Best-effort by design: canvas capture can fail (tainted canvas on a
 * cross-origin source, decode errors, unsupported codec) and the video
 * plays back fine either way - a missing poster just means the native
 * player's own default (blank until played) is used instead.
 */
export const useVideoPoster = () => {
  const posterUrl = ref('');
  const isGenerating = ref(false);

  const generatePoster = videoSrc =>
    new Promise(resolve => {
      if (!videoSrc) {
        resolve('');
        return;
      }

      isGenerating.value = true;
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';
      video.muted = true;
      video.src = videoSrc;

      const cleanup = result => {
        video.removeAttribute('src');
        video.load();
        isGenerating.value = false;
        resolve(result);
      };

      video.addEventListener('loadedmetadata', () => {
        // Seeking to 0 on some codecs yields a black frame; nudge forward.
        video.currentTime = Math.min(
          POSTER_SEEK_TIME_SECONDS,
          video.duration || POSTER_SEEK_TIME_SECONDS
        );
      });

      video.addEventListener('seeked', () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          posterUrl.value = dataUrl;
          cleanup(dataUrl);
        } catch (error) {
          // Tainted canvas (cross-origin) or decode failure - no poster.
          cleanup('');
        }
      });

      video.addEventListener('error', () => cleanup(''));
    });

  return { posterUrl, isGenerating, generatePoster };
};
